import { inject } from '@angular/core';
import type { CanActivateFn } from '@angular/router';
import { Router } from '@angular/router';
import { AuthService } from '../core/services/auth.service';
import { map, catchError, of } from 'rxjs';

/**
 * Authentication Guard
 *
 * Protects routes by checking:
 * 1. Valid Cognito session exists
 * 2. User data was loaded by app initializer
 * 3. User has at least one valid role assigned
 *
 * Note: User data is fetched by app initializer (app.config.ts)
 * This guard only validates access, it does NOT fetch user data
 */
export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.getUserBySession().pipe(
    map(session => {
      // Check if session exists and has required authentication data
      if (!session || !session.payload || !session.payload.prid) {
        // No valid session - redirect to login
        router.navigate(['/auth/login'], {
          queryParams: { returnUrl: state.url }
        });
        return false;
      }

      // Check if user data was loaded by app initializer
      const userData = authService.userDataValue;
      if (!userData) {
        // User data not loaded (not found in backend or fetch failed)
        // This means app initializer couldn't load user data
        router.navigate(['/access-denied']);
        return false;
      }

      // Check if user has valid roles assigned
      if (!userData.system_role_info || userData.system_role_info.length === 0) {
        // User has no roles assigned - redirect to access-denied
        router.navigate(['/access-denied']);
        return false;
      }

      // All checks passed - allow access
      return true;
    }),
    catchError(() => {
      // Session fetch failed - redirect to login
      router.navigate(['/auth/login'], {
        queryParams: { returnUrl: state.url }
      });
      return of(false);
    })
  );
};
